import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
// Material UI
import MuiFormControl from '@material-ui/core/FormControl';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiListSubheader from '@material-ui/core/ListSubheader';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiSelect from '@material-ui/core/Select';
// Custom
import Divider from './Divider';
import Typography from './Typography';

const SelectRenderedValue = ({ item, nullItemLabel }) => {
  if (item) {
    const { label, labelId, icon } = item;
    return (
      <div className="d-flex align-items-center text-truncate">
        {icon && (
          <div style={{ display: 'inherit', marginRight: '8px' }}>
            <icon.type
              {...icon.props}
              style={{
                width: '20px', height: '20px', marginLeft: '4px', marginRight: '8px', marginTop: '-4px', marginBottom: '-4px',
              }}
            />
          </div>
        )}
        {label || <LocalizedMessage id={labelId} />}
      </div>
    );
  }
  return (
    <div className="d-flex align-items-center text-truncate">
      {nullItemLabel}
    </div>
  );
};

SelectRenderedValue.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    labelId: PropTypes.string,
    icon: PropTypes.element,
  }),
  nullItemLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

SelectRenderedValue.defaultProps = {
  item: null,
  nullItemLabel: null,
};

const Select = ({
  label,
  value: currentValue,
  items,
  withNullItem,
  nullItemLabel,
  required,
  errorText,
  fullWidth,
  autoWidth,
  itemsMinWidth,
  margin,
  maxWidth,
  disabled,
  readOnly,
  disableUnderline,
  hideDropDownIcon,
  adornments,
  onChange,
}) => {
  // Error flag
  const error = !_isEmpty(errorText);

  // Change handler
  const handleChange = onChange ? (e) => onChange(e.target.value) : null;

  // Components props
  const style = maxWidth ? { maxWidth } : null;
  const formControlProps = {
    required,
    error,
    fullWidth,
    margin,
    style,
  };
  let selectProps = {
    value: _isNil(currentValue) ? '' : currentValue,
    autoWidth,
    disableUnderline,
    onChange: handleChange,
    displayEmpty: true,
  };
  if (readOnly) {
    selectProps = {
      ...selectProps,
      inputProps: {
        readOnly: true,
      },
    };
  }

  if (hideDropDownIcon) {
    selectProps = {
      ...selectProps,
      IconComponent: () => <div />, // eslint-disable-line react/display-name
    };
  }

  // Adornments
  const startAdornment = _get(adornments, 'start');
  const endAdornment = _get(adornments, 'end');

  return (
    <MuiFormControl {...formControlProps} disabled={disabled}>
      {label && (
        <MuiInputLabel>{label}</MuiInputLabel>
      )}
      <MuiSelect
        {...selectProps}
        renderValue={(value) => (
          <SelectRenderedValue item={_find(items, ['value', value])} nullItemLabel={nullItemLabel} />
        )}
        startAdornment={startAdornment && (
          <MuiInputAdornment position="start">
            {startAdornment}
          </MuiInputAdornment>
        )}
        endAdornment={endAdornment && (
          <MuiInputAdornment position="end">
            {endAdornment}
          </MuiInputAdornment>
        )}
      >
        {withNullItem && (
          <MuiMenuItem value={null} style={{ height: 36 }} dense>{nullItemLabel || ' '}</MuiMenuItem>
        )}
        {_map(items, ({
          key, divider, header, value, label: labelDesc, labelId, icon,
        }) => (
          (divider || header) ? (
            <MuiListSubheader key={key} value="_ignore">
              {divider ? (
                <div className="my-2">
                  <Divider />
                </div>
              ) : (
                <Typography type="selectHeader">
                  {labelDesc || <LocalizedMessage id={labelId} />}
                </Typography>
              )}
            </MuiListSubheader>
          ) : (
            <MuiMenuItem
              key={key}
              value={value}
              dense
              style={{ minWidth: itemsMinWidth }}
            >
              <div className="d-flex align-items-center">
                {icon && (
                  <div style={{ display: 'inherit', marginRight: '16px' }}>
                    <icon.type {...icon.props} />
                  </div>
                )}
                {labelDesc || <LocalizedMessage id={labelId} />}
              </div>
            </MuiMenuItem>
          )
        ))}
      </MuiSelect>
      {error && (
        <MuiFormHelperText error>
          {errorText}
        </MuiFormHelperText>
      )}
    </MuiFormControl>
  );
};

Select.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      divider: PropTypes.bool,
      header: PropTypes.bool,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      labelId: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      icon: PropTypes.element,
    }),
  ).isRequired,
  withNullItem: PropTypes.bool,
  nullItemLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  required: PropTypes.bool,
  errorText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  fullWidth: PropTypes.bool,
  autoWidth: PropTypes.bool,
  itemsMinWidth: PropTypes.number,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  maxWidth: PropTypes.number,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  hideDropDownIcon: PropTypes.bool,
  adornments: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  }),
  onChange: PropTypes.func,
};

Select.defaultProps = {
  label: null,
  value: '',
  withNullItem: false,
  nullItemLabel: null,
  required: false,
  errorText: null,
  fullWidth: true,
  autoWidth: true,
  itemsMinWidth: 100,
  margin: 'normal',
  maxWidth: null,
  disabled: false,
  readOnly: false,
  disableUnderline: false,
  hideDropDownIcon: false,
  adornments: null,
  onChange: null,
};

export default React.memo(Select);



// WEBPACK FOOTER //
// ./components/core/Select.js